import {Group, Text} from '@mantine/core'

import {BOARD_LANE_LABELS} from './board-lane-labels'
import type {BoardLane} from './board-lanes'
import classes from './dispatch-drawer.module.css'

const LANE_ACCENT_CLASS: Record<BoardLane, string> = {
    needs_action: classes.laneHintAccentNeedsAction,
    notify_drivers: classes.laneHintAccentNotifyDrivers,
    dispatched: classes.laneHintAccentDispatched,
}

export function DrawerSectionEyebrow({children}: {children: string}) {
    return <p className={classes.sectionEyebrow}>{children}</p>
}

export function DrawerOperationalSectionHeading({children}: {children: string}) {
    return <h3 className={classes.operationalSectionHeading}>{children}</h3>
}

/** Requirement row label paired with a fill count, matching crew view. */
export function DrawerRequirementHeading({
    label,
    count,
    quantity,
    satisfied,
    availabilityHint,
    onSiteQuantity = 0,
}: {
    label: string
    count: number
    quantity: number
    satisfied: boolean
    onSiteQuantity?: number
    /** Compact status when nothing assignable (e.g. “All booked elsewhere”). */
    availabilityHint?: string | null
}) {
    return (
        <Group justify="space-between" align="baseline" wrap="nowrap" gap="md">
            <Group gap={6} wrap="nowrap" style={{minWidth: 0, flex: 1}}>
                <Text size="sm" fw={600} lh={1.3} style={{flexShrink: 0}}>
                    {label}
                </Text>
                {onSiteQuantity > 0 ? (
                    <Text size="xs" c="dimmed" lh={1.3} data-testid="requirement-on-site">
                        {onSiteQuantity} already on site
                    </Text>
                ) : null}
                {availabilityHint ? (
                    <Text
                        size="xs"
                        c="orange.7"
                        lh={1.3}
                        truncate
                        data-testid="requirement-availability-hint"
                    >
                        {availabilityHint}
                    </Text>
                ) : null}
            </Group>
            <span
                className={`${classes.fillCount} ${satisfied ? classes.fillCountMet : classes.fillCountOpen}`}
            >
                {count}/{quantity}
            </span>
        </Group>
    )
}

/** Echoes the board lane header: accent bar + uppercase lane label. */
export function DrawerLaneHint({lane, detail}: {lane: BoardLane; detail?: string}) {
    return (
        <div className={classes.laneHint} data-testid="drawer-lane-hint">
            <span className={`${classes.laneHintAccent} ${LANE_ACCENT_CLASS[lane]}`} aria-hidden />
            <span className={classes.laneHintLabel}>{BOARD_LANE_LABELS[lane]}</span>
            {detail ? (
                <>
                    <span className={classes.laneHintDetail} aria-hidden>
                        ·
                    </span>
                    <span className={classes.laneHintDetail}>{detail}</span>
                </>
            ) : null}
        </div>
    )
}
